import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import * as day from 'dayjs';
import { BehaviorSubject, catchError, ReplaySubject, switchMap, map, startWith, tap, distinctUntilChanged } from 'rxjs';

import {
  MODAL_OVERLAY_DATA,
  ModalOverlayRef,
  ModalOverlayServiceCloseEventType,
} from '@app/src/app/shared/components/modal-overlay';
import { SystemAlertSnackBarService } from '@app/src/app/shared/components/system-alert-snackbar';
import { FlexTimePeriodService } from '@app/src/app/shared/services/flex-time';

import { FtmSharedMessages, PeriodSelectorModalData, PeriodSelectorModalResponse } from '../../ftm-shared.constants';

@Component({
  selector: 'mg-ftm-period-selector-modal',
  templateUrl: './ftm-period-selector-modal.component.html',
  styleUrls: ['./ftm-period-selector-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FtmPeriodSelectorModalComponent {
  public MESSAGES = FtmSharedMessages;

  private _formState = new BehaviorSubject<'idle' | 'loading'>('loading');
  public formState$ = this._formState.asObservable();

  public MIN_DATE = day();

  public form = new FormGroup({
    date: new FormControl<day.Dayjs>(day()),
    period: new FormControl<number>(null, [Validators.required]),
  });

  public periodOptions$ = this.form.get('date').valueChanges.pipe(
    startWith(day()),
    tap(() => {
      this._formState.next('loading');
      // reset the period select on date change
      this.form.get('period').setValue(null);
      this.form.get('period').markAsPristine();
    }),
    switchMap((date: day.Dayjs) => {
      return this._flexPeriods.fetchAll(date, date);
    }),
    tap(periods => {
      const initialPeriod = this._flexPeriods.getCurrentlyInProgressPeriod(periods);

      if (initialPeriod) {
        this.form.get('period').setValue(initialPeriod.id);
      }
    }),
    map(periods => {
      return periods.map(p => ({
        value: p.id,
        label: p.title,
        data: p,
      }));
    }),
    tap(() => {
      this._formState.next('idle');
    }),
    catchError(error => {
      this._systemAlertSnackBar.error('Unable to fetch periods');
      return [];
    }),
  );

  constructor(
    @Inject(MODAL_OVERLAY_DATA) public data: PeriodSelectorModalData,
    private _modalRef: ModalOverlayRef<PeriodSelectorModalResponse, PeriodSelectorModalData>,
    private _flexPeriods: FlexTimePeriodService,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
  ) {}

  public cancel(): void {
    this._modalRef.close(ModalOverlayServiceCloseEventType.CLOSE);
  }

  public async submit(selectedId: number, periods): Promise<void> {
    this._modalRef.close(ModalOverlayServiceCloseEventType.CREATE, {
      period: periods.find(p => p.value === selectedId)?.data,
    });
  }
}
